/**
 * LiveKit session adapter, shaped like the Vonage (OpenTok) API.
 *
 * The delivery call components were written directly against `OT`, and between
 * them they are ~4,000 lines. Rewriting those against livekit-client would mean
 * touching every device-picker, every event handler and the preset-MP4 path all
 * at once, with no way to land it incrementally.
 *
 * So this presents the small slice of the Vonage surface those components
 * actually use, backed by LiveKit. A call site changes from
 *
 *     const session = OT.initSession(applicationId, sessionId)
 * to
 *     const session = createSession(callData)      // picks provider off the row
 *
 * and the rest of the component keeps working.
 *
 * WHICH PROVIDER: decided by the backend, not here. The call row carries
 * `video_provider`, and MediTalk responses carry `livekit_url` with
 * `application_id` deliberately null. A stale cached bundle that predates this
 * file therefore fails its `Boolean(application_id)` guard and reports "cannot
 * connect" rather than silently dialling a dead Vonage session.
 *
 * WIRED INTO: PortalDeliveryCallWorkspace and PharmacistDriverVideoRoom. Both
 * keep their original `OT.*` branch verbatim and only take this path when the
 * call row says `video_provider === 'meditalk'`.
 */

import {
  Room,
  RoomEvent,
  Track,
  createLocalAudioTrack,
  createLocalVideoTrack,
  LocalVideoTrack,
  LocalAudioTrack,
} from 'livekit-client';

export const PROVIDER_VONAGE = 'vonage';
export const PROVIDER_MEDITALK = 'meditalk';

/**
 * Which provider a call belongs to. Read from the row the backend pinned at
 * creation — never from a client-side setting, or the two participants could
 * disagree.
 */
export const providerOf = (callData) =>
  (callData?.video_provider === PROVIDER_MEDITALK ? PROVIDER_MEDITALK : PROVIDER_VONAGE);

export const isMeditalkCall = (callData) => providerOf(callData) === PROVIDER_MEDITALK;

/**
 * Publishing defaults.
 *
 * Deliberately capped. Measured on the existing stack, clients publish three
 * simulcast layers up to 720p with no ceiling, which on a driver's metered
 * cellular connection burns data and battery for detail nobody can see in a
 * panel-sized tile. 540p at ~700kbps looks the same here.
 *
 * Drivers are moving, so smoothness matters more than sharpness; the pharmacist
 * is stationary and benefits from the opposite. Hence the per-role preference.
 */
export const PUBLISH_DEFAULTS = {
  driver: {
    resolution: { width: 960, height: 540, frameRate: 24 },
    maxBitrate: 700_000,
    degradationPreference: 'maintain-framerate',
  },
  pharmacist: {
    resolution: { width: 960, height: 540, frameRate: 24 },
    maxBitrate: 700_000,
    degradationPreference: 'maintain-resolution',
  },
};

/**
 * Audio capture settings, set explicitly rather than trusting browser defaults.
 * A driver is in a vehicle or on a street and the pharmacy is noisy, so these
 * are the highest-value settings in the whole migration for perceived quality.
 */
export const AUDIO_DEFAULTS = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
};

/**
 * LiveKit equivalent of utils/vonagePublisherQuality.js.
 *
 * That module reads the Network Information API and drops the publisher's
 * ceiling on a saveData / 2G / 3G connection. Same idea here, except LiveKit
 * takes the ceiling as publish options rather than a post-hoc setter, so this
 * returns a profile instead of mutating a publisher.
 */
const isSlowConnection = () => {
  if (typeof navigator === 'undefined') return false;
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  return Boolean(
    connection?.saveData
    || connection?.effectiveType === 'slow-2g'
    || connection?.effectiveType === '2g'
    || connection?.effectiveType === '3g'
  );
};

export const resolvePublishProfile = (role = 'pharmacist') => {
  const base = PUBLISH_DEFAULTS[role] ?? PUBLISH_DEFAULTS.pharmacist;
  if (!isSlowConnection()) return base;

  // Mirrors applyPublisherNetworkProfile's 500k -> 250k step down.
  return {
    ...base,
    resolution: { width: 640, height: 360, frameRate: 15 },
    maxBitrate: 250_000,
  };
};

/**
 * How long a remote participant may be absent before the UI treats them as
 * gone. LiveKit fires ParticipantDisconnected during ordinary reconnects, so
 * reacting to the first one would end calls that were only briefly wobbling.
 */
export const REMOTE_LEAVE_GRACE_MS = 6000;

/**
 * A LiveKit room wearing enough of the Vonage session shape for the delivery
 * call components: on/off, publish, subscribe, disconnect.
 */
class LiveKitSessionAdapter {
  constructor(callData) {
    this.callData = callData;
    this.url = callData?.livekit_url || '';
    this.room = new Room({
      adaptiveStream: true,
      dynacast: true,
      // Simulcast off. Uncapped 720p simulcast is what the measurement flagged
      // as wrong for metered connections; a single capped 540p layer is the
      // point of this migration. Per-track options repeat it so a publish that
      // does not go through this default still gets it.
      publishDefaults: {
        simulcast: false,
        videoCodec: 'vp8',
      },
    });
    this.handlers = new Map();
    this.localTracks = [];
    this.localAudioTrack = null;
    this.localVideoTrack = null;
    this.connected = false;
    this.remoteLeaveTimer = null;

    // Map LiveKit events onto the Vonage names the components already listen
    // for, so their handlers need no changes.
    this.room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
      this.emit('streamCreated', {
        stream: { track, publication, participant, id: publication?.trackSid },
      });
    });
    this.room.on(RoomEvent.TrackUnsubscribed, (track, publication, participant) => {
      this.emit('streamDestroyed', {
        stream: { track, publication, participant, id: publication?.trackSid },
      });
    });
    this.room.on(RoomEvent.Disconnected, () => {
      this.connected = false;
      this.emit('sessionDisconnected', {});
    });
    // Vonage fires videoDisabled/videoEnabled on the subscriber; LiveKit reports
    // it as a mute on the publication.
    this.room.on(RoomEvent.TrackMuted, (publication, participant) => {
      if (publication?.kind === Track.Kind.Video) {
        this.emit('videoDisabled', { publication, participant });
      }
    });
    this.room.on(RoomEvent.TrackUnmuted, (publication, participant) => {
      if (publication?.kind === Track.Kind.Video) {
        this.emit('videoEnabled', { publication, participant });
      }
    });
    // A peer briefly dropping is NOT the call ending — LiveKit fires this during
    // ordinary reconnects too. Components should treat it as advisory.
    this.room.on(RoomEvent.Reconnecting, () => this.emit('reconnecting', {}));
    this.room.on(RoomEvent.Reconnected, () => {
      this.clearRemoteLeaveTimer();
      this.emit('reconnected', {});
    });

    // Grace period: `remoteLeft` is the event a component may act on. The raw
    // participantDisconnected is deliberately not surfaced, because acting on
    // it directly is exactly the bug this guards against.
    this.room.on(RoomEvent.ParticipantConnected, () => {
      this.clearRemoteLeaveTimer();
      this.emit('remoteJoined', {});
    });
    this.room.on(RoomEvent.ParticipantDisconnected, () => {
      this.clearRemoteLeaveTimer();
      this.remoteLeaveTimer = setTimeout(() => {
        this.remoteLeaveTimer = null;
        if (this.room.numParticipants === 0) this.emit('remoteLeft', {});
      }, REMOTE_LEAVE_GRACE_MS);
    });

    // Autoplay policy: an auto-answered call has had no user gesture, so the
    // browser can refuse to play the remote audio. LiveKit reports that here
    // rather than throwing, and startAudio() is the gesture-handler fix.
    this.room.on(RoomEvent.AudioPlaybackStatusChanged, () => {
      if (!this.room.canPlaybackAudio) this.emit('autoplayBlocked', {});
    });
    this.room.on(RoomEvent.MediaDevicesError, (error) => this.emit('mediaError', { error }));
  }

  clearRemoteLeaveTimer() {
    if (this.remoteLeaveTimer) {
      clearTimeout(this.remoteLeaveTimer);
      this.remoteLeaveTimer = null;
    }
  }

  on(event, handler) {
    if (!this.handlers.has(event)) this.handlers.set(event, new Set());
    this.handlers.get(event).add(handler);
    return this;
  }

  off(event, handler) {
    this.handlers.get(event)?.delete(handler);
    return this;
  }

  emit(event, payload) {
    this.handlers.get(event)?.forEach((h) => {
      try {
        h(payload);
      } catch (error) {
        console.error(`livekitSession: handler for ${event} threw`, error);
      }
    });
  }

  /** Vonage's session.connect(token, cb) */
  async connect(token, callback) {
    try {
      if (!this.url) throw new Error('No livekit_url on the call — backend did not supply one.');
      await this.room.connect(this.url, token);
      this.connected = true;
      this.emit('sessionConnected', {});
      callback?.(null);
    } catch (error) {
      callback?.(error);
      throw error;
    }
  }

  /**
   * Publish camera and mic.
   *
   * `streamSource` carries the preset-MP4 tracks when a pharmacist has one
   * configured. Those arrive as raw MediaStreamTracks, which LiveKit accepts
   * directly — the whole reason that module ports across unchanged.
   */
  async publish({
    audioSourceId,
    videoSourceId,
    role = 'pharmacist',
    streamSource = null,
    publishAudio = true,
    publishVideo = true,
  } = {}) {
    const prefs = resolvePublishProfile(role);
    const videoPublishOptions = {
      videoEncoding: { maxBitrate: prefs.maxBitrate, maxFramerate: prefs.resolution.frameRate },
      degradationPreference: prefs.degradationPreference,
      simulcast: false,
    };
    const published = [];

    if (streamSource?.videoTrack) {
      // Raw MediaStreamTracks from the preset-MP4 module. LiveKit wraps them
      // as-is, which is why that file ports across untouched.
      const video = new LocalVideoTrack(streamSource.videoTrack);
      await this.room.localParticipant.publishTrack(video, videoPublishOptions);
      this.localVideoTrack = video;
      published.push(video);
      if (streamSource.audioTrack) {
        const audio = new LocalAudioTrack(streamSource.audioTrack);
        await this.room.localParticipant.publishTrack(audio);
        this.localAudioTrack = audio;
        published.push(audio);
      }
    } else {
      // Enable mic and camera INDEPENDENTLY: a pharmacy desktop with no webcam
      // must still get audio rather than failing the whole publish.
      try {
        const audio = await createLocalAudioTrack({
          deviceId: audioSourceId || undefined,
          ...AUDIO_DEFAULTS,
        });
        await this.room.localParticipant.publishTrack(audio);
        this.localAudioTrack = audio;
        published.push(audio);
      } catch (error) {
        console.warn('livekitSession: no microphone', error);
        this.emit('mediaError', { error, kind: 'audioinput' });
      }
      try {
        const video = await createLocalVideoTrack({
          deviceId: videoSourceId || undefined,
          resolution: prefs.resolution,
        });
        await this.room.localParticipant.publishTrack(video, videoPublishOptions);
        this.localVideoTrack = video;
        published.push(video);
      } catch (error) {
        console.warn('livekitSession: no camera', error);
        this.emit('mediaError', { error, kind: 'videoinput' });
      }
    }

    this.localTracks = published;
    if (published.length === 0) {
      throw new Error('LiveKit publish produced no tracks (no microphone and no camera).');
    }

    // Honour the mute state the component was already showing before publish.
    if (!publishAudio) await this.setMicrophoneEnabled(false);
    if (!publishVideo) await this.setCameraEnabled(false);

    return published;
  }

  /**
   * Vonage's publisher.publishAudio()/publishVideo().
   *
   * Muting the track we published rather than going through
   * localParticipant.setMicrophoneEnabled, because that manages its own track
   * and would fight with the preset-MP4 track we handed in.
   */
  async setMicrophoneEnabled(enabled) {
    if (!this.localAudioTrack) return;
    await (enabled ? this.localAudioTrack.unmute() : this.localAudioTrack.mute());
  }

  async setCameraEnabled(enabled) {
    if (!this.localVideoTrack) return;
    await (enabled ? this.localVideoTrack.unmute() : this.localVideoTrack.mute());
  }

  /** Vonage's publisher.setAudioSource(deviceId) — swaps the mic in place. */
  async setAudioSource(deviceId) {
    if (!this.localAudioTrack) throw new Error('No microphone is published.');
    await this.localAudioTrack.restartTrack({
      deviceId: deviceId || undefined,
      ...AUDIO_DEFAULTS,
    });
  }

  /**
   * Vonage's publisher.setVideoSource(deviceId). Also the switch off the
   * preset clip onto the real camera: the publication is kept, so the peer's
   * subscription is never interrupted.
   */
  async setVideoSource(deviceId, role = 'pharmacist') {
    const prefs = resolvePublishProfile(role);
    if (!this.localVideoTrack) throw new Error('No camera is published.');
    await this.localVideoTrack.restartTrack({
      deviceId: deviceId || undefined,
      resolution: prefs.resolution,
    });
  }

  /** Vonage's OT.setAudioOutputDevice(sinkId). */
  async setAudioOutputDevice(deviceId) {
    await this.room.switchActiveDevice('audiooutput', deviceId);
  }

  /** Gesture handler for autoplay-blocked remote audio. */
  async startAudio() {
    await this.room.startAudio();
  }

  get canPlaybackAudio() {
    return this.room.canPlaybackAudio;
  }

  /** Attach a subscribed track into a container, as session.subscribe did. */
  attach(track, container) {
    if (!track || !container) return null;
    const el = track.attach();
    if (el.tagName === 'VIDEO') {
      el.playsInline = true;
      el.style.cssText = 'width:100%;height:100%;object-fit:cover;';
    } else {
      // Audio elements must stay in the DOM to play, but must not occupy space.
      el.style.cssText = 'display:none;';
    }
    container.appendChild(el);
    return el;
  }

  /** Local self-view — the equivalent of Vonage's publisher.element. */
  attachLocalPreview(container) {
    if (!container || !this.localVideoTrack) return null;
    container.innerHTML = '';
    const el = this.localVideoTrack.attach();
    el.muted = true;
    el.playsInline = true;
    el.style.cssText = 'width:100%;height:100%;object-fit:cover;';
    container.appendChild(el);
    return el;
  }

  /** Vonage's session.disconnect() */
  disconnect() {
    this.clearRemoteLeaveTimer();
    try {
      this.localTracks.forEach((t) => {
        try {
          t.stop();
        } catch (error) {
          /* already stopped */
        }
      });
      this.room.disconnect();
    } catch (error) {
      console.warn('livekitSession: disconnect', error);
    }
    this.localTracks = [];
    this.localAudioTrack = null;
    this.localVideoTrack = null;
    this.connected = false;
  }
}

/**
 * MediTalk calls carry no application_id on purpose, so the Vonage credential
 * guard cannot be reused. This is the equivalent check.
 */
export const hasLiveKitCredentials = (callData) => Boolean(
  callData?.vonage_session_id && callData?.vonage_token && callData?.livekit_url
);

/**
 * Device enumeration, replacing OT.getDevices for the MediTalk path.
 * `kind` is a MediaDeviceKind: audioinput | videoinput | audiooutput.
 */
export const listLiveKitDevices = async (kind) => {
  const devices = await Room.getLocalDevices(kind, false);
  const fallbackLabel = kind === 'videoinput'
    ? 'Camera'
    : kind === 'audiooutput' ? 'Default speaker' : 'Microphone';
  return (devices || []).map((device) => ({
    kind,
    deviceId: device.deviceId,
    label: device.label || fallbackLabel,
  }));
};

/**
 * The delivery-call components pattern-match error text to decide whether a
 * failure is fatal. Those patterns were written against OpenTok wording, which
 * LiveKit does not produce, so this supplies the LiveKit-side vocabulary.
 * Exported as source strings so callers can splice them into their own regexes.
 */
export const LIVEKIT_IGNORABLE_PATTERNS = [
  'LiveKit',
  'livekit',
  'Track (?:already )?(?:published|unpublished)',
  'could not (?:createOffer|createAnswer|establish pc connection)',
  'Client initiated disconnect',
  'signal connection',
  'PublishTrackError',
  'UnexpectedConnectionState',
  'DeviceUnsupportedError',
];

export const LIVEKIT_PERMISSION_PATTERNS = [
  'MediaDeviceFailure',
  'PermissionDeniedError',
  'NotAllowedError',
  'NotFoundError',
  'DeviceUnsupportedError',
];

/**
 * Build a session for a call. Returns null for Vonage calls so the caller keeps
 * its existing OT path — that is what makes this landable incrementally.
 */
export const createLiveKitSession = (callData) =>
  (isMeditalkCall(callData) ? new LiveKitSessionAdapter(callData) : null);

export default createLiveKitSession;
